"use client";

import React, { useState } from "react";
import toast from "react-hot-toast";
import { Box, Typography } from "@mui/material";
import { Plus } from "lucide-react";
import { Button } from "@/ui";
import {
  useAddCandidateProject,
  useUpdateCandidateProject,
  useRemoveCandidateProject,
  useReorderCandidateProjects,
} from "@lib/candidates/useProjectMutations";
import {
  RightColumn,
  SectionHeader,
  SectionContent,
} from "./styles/SharedStyles";
import ExperienceCard from "@components/shared/ExperienceCard";
import ReorderRoles from "@components/shared/modals/ReorderRoles";
import EditProjects from "../modals/EditProjects";
import { ProfileSkeleton } from "./skeletons";

const Projects = ({ candidate, isImporting }) => {
  const [editingProject, setEditingProject] = useState(null);
  const [reorderOpen, setReorderOpen] = useState(false);
  const addProject = useAddCandidateProject();
  const updateProject = useUpdateCandidateProject();
  const removeProject = useRemoveCandidateProject();
  const reorderProjects = useReorderCandidateProjects();

  const projects = candidate.projects ?? [];

  const handleEditProject = (project) => {
    setEditingProject(project);
  };

  const handleUpdateProject = (updatedProject) => {
    const toastId = toast.loading("Saving...");
    updateProject.mutate(
      { candidate, project: updatedProject },
      {
        onSuccess: () => {
          toast.dismiss(toastId);
        },
        onError: () => {
          toast.dismiss(toastId);
          toast.error("Failed to save");
        },
      }
    );
  };

  const handleAddProject = () => {
    const toastId = toast.loading("Adding...");
    addProject.mutate(
      {
        candidate,
        project: { name: "New project", role: "", description: "" },
      },
      {
        onSuccess: (data) => {
          toast.dismiss(toastId);
          setEditingProject(data.data.project);
        },
        onError: () => {
          toast.dismiss(toastId);
          toast.error("Failed to add project");
        },
      }
    );
  };

  // ReorderRoles renders the {id, name, position, logo} shape it shares with
  // the Float creation flow, so map each project into that view and translate
  // the reordered result back into a plain id order.
  const reorderItems = projects.map((project) => ({
    id: project.id,
    name: project.name,
    logo: project.image,
  }));

  const handleReorderSave = (reorderedItems) => {
    const toastId = toast.loading("Saving changes...");
    reorderProjects.mutate(
      {
        candidate,
        order: reorderedItems.map((item) => item.id),
      },
      {
        onSuccess: () => {
          toast.dismiss(toastId);
        },
        onError: () => {
          toast.dismiss(toastId);
          toast.error("Failed to reorder projects");
        },
      }
    );
  };

  const handleDeleteProject = (projectId) => {
    const toastId = toast.loading("Removing project...");
    removeProject.mutate(
      { candidate, projectId },
      {
        onSuccess: () => {
          toast.dismiss(toastId);
        },
        onError: () => {
          toast.dismiss(toastId);
          toast.error("Failed to remove project");
        },
      }
    );
  };

  return (
    <Box id="candidate-section-projects">
      <SectionContent>
        <Box>
          <RightColumn>
            <SectionHeader>
              <Typography variant="h2">Projects</Typography>
            </SectionHeader>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "1.2rem", mt: 2 }}>
              {projects.map((project) => (
                <ExperienceCard
                  key={project.id}
                  selected
                  hideCheckbox
                  logoSrc={project.image}
                  title={project.name}
                  summary={project.description}
                  onReorderClick={
                    projects.length > 1 ? () => setReorderOpen(true) : undefined
                  }
                  reorderLabel="Re-order projects"
                  onEdit={() => handleEditProject(project)}
                />
              ))}
              {isImporting && (
                <Box>
                  <ProfileSkeleton />
                </Box>
              )}
              <Button
                variant="tertiary"
                size="sm"
                noPadding
                startIcon={<Plus size={16} />}
                onClick={handleAddProject}
                style={{ alignSelf: "flex-start" }}
              >
                Add project
              </Button>
            </Box>
          </RightColumn>
        </Box>
      </SectionContent>
      <EditProjects
        key={editingProject?.id}
        open={editingProject !== null}
        onClose={() => setEditingProject(null)}
        project={editingProject}
        onUpdate={handleUpdateProject}
        onDelete={handleDeleteProject}
        candidate={candidate}
      />
      <ReorderRoles
        open={reorderOpen}
        onClose={() => setReorderOpen(false)}
        roles={reorderItems}
        onSave={handleReorderSave}
        title="Re-order projects"
        description="Drag projects up or down to reorder them — the new order will be reflected on the candidate's profile."
        successMessage="Project order updated"
      />
    </Box>
  );
};

export default Projects;
